import React, { useEffect, useState } from 'react'
import { CFormLabel } from '@coreui/react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useAuth } from '../contexts/AuthContext';
import { useReport } from '../contexts/ReportContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { colors, formatDateToBackend, openFileWithLoading } from '../components/Helper'
import { reportNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2'
import PieChart from '../components/PieChart';
import LineChart from '../components/LineChart';
import PageHeader from '../components/PageHeader'
import DateRangeSelection from '../components/DateRangeSelection';

function HourReport() {
    const { HourReport, receiptReportCharts, hourReportPDF, hourReportCSV, } = useReport();
    const { currency } = useCompanySetting();
    const { restaurantId } = useAuth();

    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(currentDate.getDate() - 1);

    const [dates, setDates] = useState([yesterdayDate, currentDate]);
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);
    const [reportList, setReportList] = useState([])
    const [paymentChartData, setPaymentChartData] = useState([])
    const [hourlyRevenue, setHourlyRevenue] = useState({
        x: [],
        y: []
    })
    const [hourlyReceipt, setHourlyReceipt] = useState({
        x: [],
        y: []
    })

    const startDate = (dates.length > 0 && dates[0]) ? formatDateToBackend(dates[0]) : null
    const endDate = (dates.length > 0 && dates[1]) ? formatDateToBackend(dates[1]) : null

    const fetchReceiptReport = async () => {
        try {
            setIsLoading(true);
            const reports = await HourReport(sortField, sortOrder, startDate, endDate);
            if (reports.data.length > 0) {
                setReportList(reports.data)
            } else {
                setReportList([])
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching Hour reports', error);
        }
    };

    const fetchHourReportCharts = async () => {
        try {
            setIsLoading(true);

            const reports = await receiptReportCharts(startDate, endDate);
            if (reports.paymentChart.length > 0) {
                const paymentData = reports.paymentChart.map((item, index) => ({
                    id: item.id,
                    value: parseFloat(item.value),
                    label: item.label,
                    color: colors[index % colors.length]
                }));

                setPaymentChartData(paymentData);
            } else {
                setPaymentChartData([]);
            }

            if (reports.hourlyRevenue.length > 0) {
                const hours = reports.hourlyRevenue.map(item => item.hour);
                const revenues = reports.hourlyRevenue.map(item => parseFloat(item.revenue));

                setHourlyRevenue({
                    x: hours,
                    y: revenues
                })
            } else {
                setHourlyRevenue({
                    x: [],
                    y: []
                })
            }

            if (reports.hourlyOrders.length > 0) {
                const hours = reports.hourlyOrders.map(item => item.hour);
                const orders = reports.hourlyOrders.map(item => parseFloat(item.orders));

                setHourlyReceipt({
                    x: hours,
                    y: orders
                })
            } else {
                setHourlyReceipt({
                    x: [],
                    y: []
                })
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching Hour report charts', error);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchHourReportCharts();
        }
    }, [restaurantId, startDate, endDate]);

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchReceiptReport();
        }
    }, [sortField, sortOrder, restaurantId, startDate, endDate]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchReceiptReport()
    };

    const generatePDF = async (pdfType) => {
        openFileWithLoading({
            apiCall: hourReportPDF,
            apiParams: [startDate, endDate, pdfType],
            fileType: 'pdf',
            loadingMessage: `Generating ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF...`,
            errorMessage: `Failed to generate ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF`,
        });
    }

    const generateCSV = async (csvType) => {
        openFileWithLoading({
            apiCall: hourReportCSV,
            apiParams: [startDate, endDate, csvType],
            fileType: 'csv',
            loadingMessage: `Generating ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV...`,
            errorMessage: `Failed to generate ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV`,
        });
    }

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Hour Reports'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-between'>
                    <p className='pt-2'>
                        Print or download the hour report: <span className='view_button' onClick={() => generatePDF('summary')}>Summary PDF</span> / <span className='view_button' onClick={() => generateCSV('summary')}>CSV</span>
                    </p>
                    <DateRangeSelection
                        dates={dates}
                        setDates={setDates}
                    />
                </div>
                <div className='px-4 py-2'>
                    <div className='report-chart-section d-flex justify-content-between mt-3'>
                        {
                            hourlyRevenue.x.length > 0 && hourlyRevenue.y.length > 0 && (
                                <div className='w-100 text-center me-5'>
                                    <CFormLabel>Revenue</CFormLabel>
                                    <LineChart
                                        x={hourlyRevenue.x}
                                        y={hourlyRevenue.y}
                                        yLabel={'Revenue'}
                                    />
                                </div>
                            )
                        }
                        {
                            hourlyReceipt.x.length > 0 && hourlyReceipt.y.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Receipt</CFormLabel>
                                    <LineChart
                                        x={hourlyReceipt.x}
                                        y={hourlyReceipt.y}
                                        yLabel={'Receipts'}
                                    />
                                </div>
                            )
                        }
                        {
                            paymentChartData && paymentChartData.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Payment</CFormLabel>
                                    <PieChart
                                        data={paymentChartData}
                                    />
                                </div>
                            )
                        }
                        {
                            paymentChartData && paymentChartData.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Payment Amount</CFormLabel>
                                    <PieChart
                                        data={paymentChartData}
                                    />
                                </div>
                            )
                        }
                    </div>
                    <div className='border-bottom mb-3'>
                        <p className='labour-clock-time'>Hour Report</p>
                    </div>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark report-table"
                            lazy
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            value={reportList}
                            dataKey="id"
                            emptyMessage="No Hour reports found."
                        >
                            <Column
                                field="startHour"
                                header="Starts At"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="endHour"
                                header="Ends At"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="receiptItems"
                                header="#Receipt Items"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="taxAmount"
                                header="Tax"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.taxAmount === 0 ? `[No revenues]` : `${currency}${rowData.taxAmount}`}
                                    </div>
                                )}
                            />
                            <Column
                                field="receiptCreated"
                                header="#Receipt Created"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="receiptClosed"
                                header="#Receipt Closed"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="revenue"
                                header="Revenue"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.revenue === 0 ? `${currency}0.00` : `${currency}${rowData.revenue}`}
                                    </div>
                                )}
                            />
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default HourReport